import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  ArrowRight,
  Search,
  CheckCircle,
  Globe,
  Shield,
  Lightbulb,
  Users,
  Scale,
  Sparkles,
  CreditCard,
  Zap,
} from "lucide-react";
import { HeroSection } from "./HeroSection";
import { StepsSection } from "./StepsSection";
import { FeaturesSection } from "./FeaturesSection";

const Index = () => {
  const navigate = useNavigate();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const benefits = [
    "Generate unlimited brand names",
    "Check domain availability instantly",
    "Get trademark risk analysis",
    "Access AI-powered insights",
    "No credit card required to start",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 font-inter">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-teal-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center hidden sm:flex">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="hidden sm:inline text-2xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                Name Pilot
              </span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Button
                variant="ghost"
                className="text-teal-700 hover:text-teal-800 hover:bg-teal-50 text-base sm:text-lg font-semibold px-2 sm:px-4"
                onClick={() => scrollToSection("features")}
              >
                Features
              </Button>
              <Button
                variant="ghost"
                className="text-teal-700 hover:text-teal-800 hover:bg-teal-50 text-base sm:text-lg font-semibold px-2 sm:px-4"
                onClick={() => scrollToSection("pricing")}
              >
                Pricing
              </Button>
              <Button
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white text-base sm:text-lg font-semibold px-2 sm:px-4"
                onClick={() => navigate("/auth")}
              >
                Login / Signup
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <HeroSection />
      <StepsSection />

      {/* Add id to features section */}
      <div id="features">
        <FeaturesSection />
      </div>

      {/* Combined Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-teal-600 to-cyan-700 border-0 text-white overflow-hidden relative">
            {/* Background Elements */}
            <div className="absolute inset-0">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-2xl transform -translate-x-1/2 translate-y-1/2"></div>
            </div>

            <CardContent className="relative p-12 lg:p-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Left Column - Content */}
                <div>
                  <div className="flex items-center space-x-2 mb-6">
                    <Sparkles className="w-6 h-6 text-teal-200" />
                    <span className="text-teal-200 font-medium">
                      Simple Pricing
                    </span>
                  </div>

                  <h2 className="text-3xl lg:text-4xl font-bold mb-6 leading-tight">
                    Start Free, Pay Only for What You Use
                  </h2>

                  <p className="text-xl text-teal-100 mb-4 leading-relaxed">
                    Every new user gets{" "}
                    <span className="font-semibold text-white">
                      6 free credits
                    </span>{" "}
                    to explore Name Pilot - that's 2 full reports or 6
                    individual reports completely free.
                  </p>

                  <p className="text-lg text-teal-100 mb-8 leading-relaxed">
                    Then purchase credit packs as you need them. No
                    subscriptions, no commitments.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    <Button
                      size="lg"
                      className="bg-white text-teal-700 hover:bg-gray-50 px-8 py-3 text-lg font-semibold"
                      onClick={() => navigate("/auth")}
                    >
                      Start Free Trial
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    {/* <Button
                      size="lg"
                      className="bg-white text-teal-700 hover:bg-gray-50 px-8 py-3 text-lg font-semibold"
                      onClick={() => navigate("/auth")}
                    >
                      Schedule Demo
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button> */}
                  </div>

                  <p className="text-teal-200 text-sm">
                    🚀 Get started in under 2 minutes • ✨ No setup required
                  </p>
                </div>

                {/* Right Column - Benefits */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                  <h3 className="text-xl font-bold mb-6">What you'll get:</h3>
                  <div className="space-y-4">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-teal-400 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-4 h-4 text-teal-900" />
                        </div>
                        <span className="text-white">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-8 border-t border-white/20">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-teal-200">Free Trial</span>
                      <span className="font-bold text-2xl">$0</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-teal-200">Then starting at</span>
                      <span className="font-bold">$6 for 10 credits</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Credit System Explanation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 mt-16">
            <Card className="border-teal-100 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Individual Reports
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Name Ideation Report</span>
                    <Badge className="bg-teal-100 text-teal-700 border-0">
                      1 Credit
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Trademark Report</span>
                    <Badge className="bg-teal-100 text-teal-700 border-0">
                      1 Credit
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Domain Report</span>
                    <Badge className="bg-teal-100 text-teal-700 border-0">
                      1 Credit
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-cyan-100 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center mb-6">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Full Report
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Complete Analysis</span>
                    <Badge className="bg-cyan-100 text-cyan-700 border-0">
                      3 Credits
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">
                    Includes all three reports: Name Ideation, Trademark
                    Analysis, and Domain Research in one comprehensive package.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Credit Packs */}
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Replenish Credits
            </h3>
            <p className="text-gray-600">
              Purchase credit packs when you need more reports
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-teal-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Starter Pack
                </h4>
                <div className="text-3xl font-bold text-teal-700 mb-2">$6</div>
                <div className="text-gray-600 mb-6">10 Credits</div>
                <div className="space-y-2 mb-6 text-sm text-gray-600">
                  <div>• 3 Full Reports</div>
                  <div>• 10 Individual Reports</div>
                  <div>• Credits never expire</div>
                </div>
                <Button
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white hover:from-teal-700 hover:to-cyan-700"
                  onClick={() => navigate("/auth")}
                >
                  Purchase Starter Pack
                </Button>
              </CardContent>
            </Card>

            <Card className="border-cyan-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white px-4 py-1">
                  Best Value
                </Badge>
              </div>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Pro Pack
                </h4>
                <div className="text-3xl font-bold text-cyan-700 mb-2">$10</div>
                <div className="text-gray-600 mb-6">20 Credits</div>
                <div className="space-y-2 mb-6 text-sm text-gray-600">
                  <div>• 6+ Full Reports</div>
                  <div>• 20 Individual Reports</div>
                  <div>• Credits never expire</div>
                  <div className="text-cyan-700 font-medium">
                    • Save $2 vs Starter
                  </div>
                </div>
                <Button
                  className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 text-white hover:from-cyan-700 hover:to-teal-700"
                  onClick={() => navigate("/auth")}
                >
                  Purchase Pro Pack
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-500 text-sm">
              💳 Secure payment processing • 🔄 Credits never expire • 📧
              Instant delivery
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">Name Pilot</span>
              </div>
              <p className="text-teal-200 mb-4 max-w-md">
                AI-powered brand name generation and analysis for founders,
                creatives, and IP attorneys.
              </p>
              <p className="text-teal-300 text-sm">
                © 2024 Name Pilot. All rights reserved.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-teal-200">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    API
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-teal-200">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Contact
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Privacy
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
